<template>
    <div class="image-upload-wrap">
        <div :class="['image-upload-box', urlfull ? 'edit' : 'add']">
            <div class="image-preview-box" v-loading="uploadLoading">
                <el-image :src="urlfull" fit="contain" :preview-src-list="[urlfull]">
                    <div slot="error" class="image-slot">
                        <i class="icon-photo"></i>
                    </div>
                </el-image>
            </div>
            <div class="image-upload-bar">
                <el-upload ref="imageUpload" :disabled="disabled || uploadLoading" action :drag="drag" :accept="accept"
                    :http-request="() => { }" :show-file-list="false" :before-upload="beforeUploadHandle">
                    <span class="icon"></span>
                    <div class="button">
                        <div class="point"></div>
                        <el-button type="primary" icon="">{{ urlfull ? '重新上传' : '上传封面' }}</el-button>
                    </div>
                </el-upload>
            </div>
        </div>
        <div class="image-upload-tips">
            <slot name="tips">格式：JPG、JPEG、GIF、PNG <br />大小：不得大于15M</slot>
        </div>
        <!-- 裁切模块 -->
        <el-dialog title="裁剪图片" class="image-cropper-wrap" :visible.sync="dialogVisible" :close-on-click-modal="false"
            :close-on-press-escape="false" width="600px" append-to-body>
            <div :class="['cropper-content', isWatermark && hasWatermarkModel ? 'watermark' : '']"
                :style="{ height: '600px' }">
                <vue-cropper ref="cropper" :img="copperOptions.base64" fixed auto-crop :canScale="false" :outputSize="1"
                    output-type="png" :fixed-number="copperOptions.fixedNumber" full fixedBox :canMoveBox="false" centerBox
                    :auto-crop-width="copperOptions.cropWidth" :auto-crop-height="copperOptions.cropWidth"
                    :enlarge="copperOptions.enlarge"></vue-cropper>
            </div>
            <template v-if="progress && progress < 100 && uploadLoading">
                <el-progress :percentage="progress"></el-progress>
            </template>
            <div slot="footer" class="dialog-footer">
                <div class="left">
                    <el-button type="text" v-if="scale" icon="el-icon-zoom-in" @click="changeScale(1)"></el-button>
                    <el-button type="text" v-if="scale" icon="el-icon-zoom-out" @click="changeScale(-1)"></el-button>
                    <el-button type="text" icon="el-icon-refresh-left" @click="rotateLeft"></el-button>
                    <el-button type="text" icon="el-icon-refresh-right" @click="rotateRight"></el-button>
                    <el-switch v-if="isWatermark" style="margin-left: 20px" v-model="hasWatermarkModel" :active-value="1"
                        :inactive-value="0" active-text="含发行LOGO"></el-switch>
                </div>
                <div class="right">
                    <el-button v-if="!crop" type="primary" @click="uploadFileAction(sourceImage.file)"
                        :loading="uploadLoading">原图上传</el-button>
                    <el-button type="primary" @click="finish" :loading="uploadLoading">裁剪上传</el-button>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { uploadImg } from '@/api/file';
import { VueCropper } from '../cropper';

// 裁切比例配置
const FIXEDNUMBER = {
    '1:1': [400, 400],
    '4:3': [400, 300],
    '2:3': [400, 600],
};
export default {
    components: { VueCropper },
    props: {
        // 图片相对路径
        url: {
            type: String,
            default: () => '',
        },
        // 是否添加水印
        hasWatermark: {
            type: Number,
        },
        //允许上传格式
        accept: {
            type: String,
            default: 'image/png,image/jpeg,image/gif',
        },
        fixedNumber: {
            type: String,
            default: '1:1',
        },
        crop: {
            type: Boolean,
            default: false,
        },
        size: {
            type: Number,
            default: 15,
        },
        drag: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        minWidth: {
            type: Number,
            default: 0,
        },
        minHeight: {
            type: Number,
            default: 0,
        },
        scale: {
            type: Boolean,
            default: false,
        },
        isWatermark: {
            type: Boolean,
            default: false,
        },
        circle: {
            type: Number,
            default: 100,
        },
        btnText: {
            type: Object,
            default: () => {
                return { add: '点击上传', update: '重新上传' };
            },
        },
    },
    computed: {
        imageUrl() {
            return this.$imageUrl || (this.$store.state.config && this.$store.state.config.imageUrl);
        },
        urlfull() {
            return this.url ? this.imageUrl + this.url : '';
        },
    },
    data() {
        return {
            uploadLoading: false,
            bytesVerified: false,
            progress: null,
            dialogVisible: false,
            copperOptions: {
                base64: null,
                fixedNumber: [1, 1],
                cropWidth: 0,
                cropHeight: 0,
                enlarge: '',
                aspectRatio: [], // 裁切框与图片的宽高比例
            },
            // 原图片信息
            sourceImage: {
                width: 0,
                height: 0,
                base64: null,
                file: null,
            },
            hasWatermarkModel: 1,
        };
    },
    watch: {
        fixedNumber: {
            immediate: true,
            handler() {
                const current = FIXEDNUMBER[this.fixedNumber];
                if (!current) return this.$message.error('裁切图片初始数值设置失败');
                this.copperOptions.fixedNumber = current;
                this.copperOptions.cropWidth = current[0];
                this.copperOptions.cropHeight = current[1];
            },
        },
        isWatermark: {
            immediate: true,
            handler(val) {
                if (!val) this.hasWatermarkModel = 0;
            },
        },
    },
    methods: {
        resetRatio() {
            const { cropWidth, cropHeight } = this.copperOptions;
            let { width, height } = this.sourceImage;
            if (width && cropWidth) this.copperOptions.aspectRatio[0] = width / cropWidth;
            if (height && cropHeight) this.copperOptions.aspectRatio[1] = height / cropHeight;
            if (this.copperOptions.aspectRatio.length == 2) {
                this.copperOptions.enlarge =
                    this.copperOptions.aspectRatio[0] > this.copperOptions.aspectRatio[1]
                        ? parseInt(this.copperOptions.aspectRatio[0])
                        : parseInt(this.copperOptions.aspectRatio[1]);
            }
        },
        //上传进度
        setProgress(ev) {
            this.progress = Math.floor((ev.loaded / ev.total) * 50);
            if (Math.floor(ev.loaded / ev.total) == 1) {
                let t = setInterval(() => {
                    if (this.progress < 99 && this.uploadLoading) {
                        this.progress += 1;
                    } else {
                        clearInterval(t);
                    }
                }, 1000);
            }
        },
        /**
         * 获取图片宽高
         * @param {*} file 图片流
         * @return {Promise}
         */
        imageWH(file) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    var data = e.target.result;
                    var image = new Image();
                    image.onload = () => {
                        resolve({ width: image.width, height: image.height, base64: data, file });
                    };
                    image.onerror = () => {
                        this.$message.error('图片文件不是JPG,JPEG,PNG 格式，请将图片转成支持的格式，请不要修改拓展名。');
                    };
                    image.src = data;
                };
                reader.readAsDataURL(file);
            });
        },
        //上传验证
        async beforeUploadHandle(file) {
            // eslint-disable-next-line no-async-promise-executor
            return new Promise(async (resolve, reject) => {
                //格式检查
                let _accept = this.accept.split(',') || [];
                _accept.map((item, index) => {
                    _accept[index] = item.trim();
                });
                const isFormat = _accept.find((v) => v == file.type) ? true : false;
                if (!isFormat) {
                    this.$message.error('文件格式错误,请重新上传');
                    return reject();
                }

                //大小检查
                const isSize = file.size / 1024 / 1024 <= this.size ? true : false;
                if (!isSize) {
                    this.$message.error(`文件不能大于${this.size}M,请重新上传`);
                    return reject();
                }

                // 判断尺寸大小
                const image = await this.imageWH(file);
                this.sourceImage = image;
                if (this.minWidth && this.minHeight) {
                    if (image.width < this.minWidth || image.height < this.minHeight) {
                        this.$message.error(`请上传不小于${this.minWidth}*${this.minHeight}像素的图片,但不能超过6000*6000像素`);
                        return reject();
                    }
                }
                if (image.width > 6000 && image.height > 6000) {
                    this.$message.error(`请上传不小于${this.minWidth}*${this.minHeight}像素的图片,但不能超过6000*6000像素`);
                    return reject();
                }

                // 弹出裁切框
                this.resetRatio();
                this.copperOptions.base64 = image.base64;
                this.dialogVisible = true;
                resolve();
            })
        },
        /**
         * base64 转为file类型
         * @param dataurl
         * @param filename
         * @returns {File}
         */
        dataURLtoFile(dataurl, filename) {
            let arr = dataurl.split(','),
                mime = arr[0].match(/:(.*?);/)[1],
                bstr = atob(arr[1]),
                n = bstr.length,
                u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new File([u8arr], filename, { type: mime });
        },
        /**
         *
         * @param {File} file 文件对象
         * @param {Boolean} cutting 是否是裁切图片
         */
        async uploadFileAction(file, cutting) {
            this.uploadLoading = true;
            this.progress = 0;

            let res;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('waterMark', this.hasWatermarkModel);
            formData.append('cutting', cutting ? 1 : 0);
            if (cutting) {
                const { x, y, width, height } = this.calculateCuttingCoordinate();
                formData.append('cuttingStartPos', `${x},${y}`);
                formData.append('cuttingStartSize', `${width},${height}`);
            }
            res = await uploadImg(formData, (ev) => {
                this.setProgress(ev);
            })
                .catch(() => {
                    this.$message.error('上传失败');
                })
                .finally(() => {
                    this.progress = 99;
                    setTimeout(() => (this.uploadLoading = false), 800);
                    this.dialogVisible = false;
                });

            const { state, result, msg } = res.data;
            if (state && state == 1 && result) {
                await new Promise((resolve) => setTimeout(resolve, 1000)); // cdn图片有时候还未更新上，导致显示图片失败
                this.$emit('update:url', result.url);
                this.$emit('update:hasWatermark', this.hasWatermarkModel);
                this.$emit('onSuccess', result);
            } else {
                this.$message.error(msg || '上传失败');
            }
        },
        // 将base64的图片转换为file文件
        convertBase64UrlToBlob(urlData) {
            let bytes = window.atob(urlData.split(',')[1]); //去掉url的头，并转换为byte
            //处理异常,将ascii码小于0的转换为大于0
            let ab = new ArrayBuffer(bytes.length);
            let ia = new Uint8Array(ab);
            for (var i = 0; i < bytes.length; i++) {
                ia[i] = bytes.charCodeAt(i);
            }
            return new Blob([ab], { type: 'image/png' });
        },
        // 计算裁切坐标
        calculateCuttingCoordinate() {
            const { aspectRatio } = this.copperOptions;
            const imageCoordinate = this.$refs.cropper.getImgAxis(); // 图片坐标
            const CropCoordinate = this.$refs.cropper.getCropAxis(); // 裁切框坐标
            const minRatio = aspectRatio[0] > aspectRatio[1] ? aspectRatio[1] : aspectRatio[0];
            let x = aspectRatio[0] < aspectRatio[1] ? 0 : parseInt((CropCoordinate.x1 - imageCoordinate.x1) * minRatio) - 5;
            x = x > 0 ? x : 0;
            let y = aspectRatio[1] < aspectRatio[0] ? 0 : parseInt((CropCoordinate.y1 - imageCoordinate.y1) * minRatio) - 5;
            y = y > 0 ? y : 0;
            const width = CropCoordinate.x2 - CropCoordinate.x1;
            const height = CropCoordinate.y2 - CropCoordinate.y1;
            return {
                x,
                y,
                width: parseInt(width * minRatio),
                height: parseInt(height * minRatio),
            };
        },
        //完成裁剪
        finish() {
            this.$refs.cropper.getCropBlob(() => {
                this.uploadFileAction(this.sourceImage.file, true);
            });
        },
        //裁剪图片放大、缩小
        changeScale(num) {
            num = num || 1;
            this.$refs.cropper.changeScale(num);
        },
        //裁剪图片左旋转
        rotateLeft() {
            this.$refs.cropper.rotateLeft();
        },
        //裁剪图片右旋转
        rotateRight() {
            this.$refs.cropper.rotateRight();
        },
    },
};
</script>

<style lang="scss">
.image-upload-wrap {

    .image-upload-box {

        .el-image {
            width: 100%;
            height: 100%;
        }

        &.edit {
            .image-upload-bar .icon {
                background: url('~@/assets/img/upload/edit.svg') no-repeat center;
            }
        }
    }

    .el-loading-mask {
        background-color: rgba(0, 0, 0, 0.8);

        .el-loading-spinner {
            margin-top: -40px;

            &::after {
                content: "拼命上传中";
                color: #fff;
                display: block;
                line-height: 20px;
            }

            .path {
                stroke: #fff;
            }
        }
    }

    .image-preview-box {
        width: 200px;
        height: 200px;
        position: relative;
        border-radius: 4px;
        overflow: hidden;
        background: url('~@/assets/img/upload/bg.png') no-repeat center;
        background-size: cover;

        .el-image {
            width: 100%;
            height: 100%;

            .image-slot {
                display: none;
            }
        }
    }

    .image-upload-bar {
        position: relative;
        line-height: 0px;
        height: 0px;
        z-index: 9;

        .icon {
            background: url('~@/assets/img/upload/add.svg') no-repeat center;
            background-size: cover;
            content: "";
            width: 30px;
            height: 30px;
            position: absolute;
            bottom: 0px;
            left: 170px;
            z-index: 9;
        }

        .button {
            display: none;
        }
    }

    .image-upload-tips {
        font-size: 12px;
        color: $-text-500;
        line-height: 24px;
        margin-top: 10px;
    }



    &.horizontal {
        display: flex;

        .image-upload-tips {
            margin-left: 30px;
            flex: 1;
            margin-top: 0px;
        }
    }

    &.no-tips {
        .image-upload-tips {
            display: none;
        }
    }

    &.album-cover {
        display: flex;
        align-items: center;
        background: $--input-background-color;
        border-radius: 4px;
        border: 1px dashed $--border-color-base;

        &:hover {
            border: 1px dashed $-text-950;
        }

        .image-preview-box {
            width: 148px;
            height: 148px;
            background: url('~@/assets/img/upload/zhan_pic@2x.png') no-repeat center;
            background-size: cover;
        }

        .image-upload-tips {
            margin: 0 15px;
        }

        .image-upload-bar {
            position: absolute;
            width: 100%;
            height: 100%;
            left: 0px;
            top: 0px;
            opacity: 0;

            .icon {
                width: 100%;
                height: 100%;
                left: 0px;
                background: none;
            }

            .button {
                display: flex;
                position: absolute;
                right: 20px;
                left: 168px;
                align-items: center;
                top: 50px;

                .point {
                    flex: 1;
                    position: relative;
                    height: 4px;
                    overflow: hidden;
                    margin-right: 10px;

                    &::before {
                        content: "..............";
                        position: absolute;
                        top: -10px;
                        left: 0px;
                        color: $-text-950;
                        line-height: 16px;
                        font-weight: bold;
                        font-size: 16px;
                        letter-spacing: 3px;
                    }
                }
            }
        }

        &:hover {
            .image-upload-bar {
                opacity: 1;

                .icon {
                    display: none;
                }
            }

            .image-upload-tips {
                display: none;
            }
        }
    }

    &.min {
        .image-preview-box {
            width: 160px;
            height: 160px;
        }

        .image-upload-bar .icon {
            left: 130px;
        }
    }
}

.image-cropper-wrap {

    .dialog-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .el-button--text {
            padding: 0px;
            margin: 0px;
        }

        .left {
            display: flex;
            align-items: center;

            i {
                font-size: 24px;
            }
        }
    }

    .watermark {
        .cropper-crop-box::after {
            content: "";
            position: absolute;
            width: 80px;
            height: 80px;
            background: url('~@/assets/img/watermark.svg') no-repeat center;
            background-size: cover;
            bottom: 0px;
            left: -1px;
        }
    }

}
</style>
